import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import { WFM_MODULES } from '../../config/wfmModules';
import usePopTransition, { MODAL_CLOSE_MS } from '../common/usePopTransition';
import ModuleIcon from '../common/ModuleIcon';
import AdminIcon from '../Admin/AdminIcons';
import ProtectedImage from '../AgentDossier/ProtectedImage';
import { SkeletonBlock } from '../common/Skeleton';
import {
  KINDS, PER_KIND, adminEntries, callieEntries, highlightParts, linkEntries, pageEntries, personEntries, queryTokens, searchLocal
} from './searchEntries';
import './GlobalSearch.css';

const MIN_QUERY = 2;
const DEBOUNCE_MS = 180;
const RECENT_MAX = 6;
const NONE = { callie: [], people: [] };

const recentKey = (user) => `eops_search_recent:${(user && user.id) || 'anon'}`;

const readRecent = (user) => {
  try {
    const list = JSON.parse(window.localStorage.getItem(recentKey(user)) || '[]');
    return Array.isArray(list) ? list.filter((e) => e && e.id && e.title).slice(0, RECENT_MAX) : [];
  } catch (err) {
    return [];
  }
};

// Only what is needed to draw and open the entry: never a snippet, so a private article's words are not kept on this computer.
export const writeRecent = (user, entry) => {
  try {
    const keep = { id: entry.id, kind: entry.kind, title: entry.title, subtitle: entry.subtitle, to: entry.to, href: entry.href, moduleId: entry.moduleId, photoUrl: entry.photoUrl, adminKind: entry.adminKind };
    const next = [keep, ...readRecent(user).filter((e) => e.id !== entry.id)].slice(0, RECENT_MAX);
    window.localStorage.setItem(recentKey(user), JSON.stringify(next));
  } catch (err) { /* private mode: nothing is remembered, and nothing is lost */ }
};

const PANEL_GAP = 8;
const PANEL_MARGIN = 8;
const PANEL_WIDTH_REM = 42; // .gs__panel in GlobalSearch.css

// Where the panel hangs: just under the navbar button, its right edge on the button's right edge (the same corner the navbar's
// other menus grow from). Null when the button is not on screen, and the stylesheet then centres the panel near the top.
const anchorFor = (button) => {
  if (!button || typeof window === 'undefined') return null;
  const rect = button.getBoundingClientRect();
  if (!rect.width && !rect.height) return null;
  const viewport = document.documentElement.clientWidth;
  const rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
  const width = Math.min(PANEL_WIDTH_REM * rem, viewport - 2 * PANEL_MARGIN); // the same width the stylesheet gives the panel
  // On a phone the buttons beside the magnifier would push the panel off the left edge, so it never goes further than the screen allows.
  const right = Math.min(Math.max(PANEL_MARGIN, viewport - rect.right), viewport - PANEL_MARGIN - width);
  return { top: Math.round(rect.bottom + PANEL_GAP), right: Math.round(right) };
};

export const isTypingTarget = (el) => Boolean(el) && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

export const Highlight = ({ text, tokens }) => (
  <>
    {highlightParts(text, tokens).map((part, i) => (
      part.hit ? <mark key={i} className="gs__mark">{part.text}</mark> : <React.Fragment key={i}>{part.text}</React.Fragment>
    ))}
  </>
);

export function RowIcon({ entry }) {
  if (entry.kind === 'person') {
    const initial = (entry.title || '?').trim().charAt(0).toUpperCase();
    return (
      <span className="gs__avatar">
        {entry.photoUrl ? <ProtectedImage url={entry.photoUrl} alt="" fallback={initial} /> : initial}
      </span>
    );
  }
  if (entry.moduleId) {
    const mod = WFM_MODULES.find((m) => m.id === entry.moduleId);
    if (mod) return <span className="gs__icon"><ModuleIcon mod={mod} className="gs__module-icon" size={20} /></span>;
  }
  const name = entry.kind === 'callie' ? 'note'
    : entry.kind === 'link' ? 'link'
      : entry.kind === 'admin' ? (entry.adminKind === 'action' ? 'action' : (entry.adminKind === 'page' ? (entry.icon || 'setting') : 'setting'))
        : 'chevron';
  return <span className="gs__icon"><AdminIcon name={name} size={16} /></span>;
}

/**
 * The global search: a box in the navbar that opens a panel of suggestions as you type, like the search in Confluence.
 *
 * It finds the pages of the suite, Callie articles, people, Admin settings and the external links, and only those this person
 * may open. The pages and Admin settings are listed on this screen from the same rules the navbar uses; Callie articles and people
 * come from GET /api/search, which applies the permissions on the server (lib/global-search.js). With nothing typed it shows the
 * last things opened from here, then the modules.
 *
 * Ctrl+K (Cmd+K) or / opens it, except inside Admin, where those keys belong to Admin's own search; the button works everywhere.
 *
 * The panel never scrolls (Ivan, 2026-11-04): it starts with up to five of each kind and takes a row from every kind until what is on show fits. Enter, or
 * "See all results", opens the full search page (/search, GlobalSearchPage.js) with everything found; arrow keys and then Enter open the highlighted one.
 */
export default function GlobalSearch({ compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState(NONE);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const [tools, setTools] = useState(null);
  const [recent, setRecent] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const [fit, setFit] = useState({ key: null, n: 0 }); // how many rows in all there is room for
  const moved = useRef(false); // the arrow keys have moved the highlight: Enter then opens it instead of the full page
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const returnFocus = useRef(null);
  const sequence = useRef(0);
  const pop = usePopTransition(open, MODAL_CLOSE_MS);
  const tokens = useMemo(() => queryTokens(query), [query]);
  const typed = tokens.join(' ').length >= MIN_QUERY;

  const close = useCallback(() => setOpen(false), []);

  // Ctrl+K and / (not while typing in a field, and not in Admin, whose own search owns them).
  useEffect(() => {
    const onKey = (e) => {
      if (pathname.startsWith('/admin')) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && !e.altKey && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === '/' && !mod && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pathname]);

  // The page stops scrolling first (so the button cannot move under the panel), then the button is measured, before the first paint
  // and again if the window changes size while it is open.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const place = () => setAnchor(anchorFor(buttonRef.current));
    place();
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
      document.body.style.overflow = previous;
    };
  }, [open]);

  // On opening: an empty box, the last things opened, the links (fetched once), and focus in the box. On closing: focus goes back.
  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    setQuery('');
    setActive(0);
    setRemote(NONE);
    setFailed(false);
    setRecent(readRecent(user));
    const t = window.setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    return () => {
      window.clearTimeout(t);
      const back = returnFocus.current;
      if (back && back.focus && document.contains(back)) back.focus();
    };
  }, [open, user]);

  useEffect(() => {
    if (!open || tools !== null) return;
    // No links in search without the External tools box (the server sends none then either).
    if (!hasPermission(user, 'home.external_tools')) { setTools([]); return; }
    let cancelled = false;
    api.get('/api/external-tools')
      .then((res) => { if (!cancelled) setTools((res.data && res.data.groups) || []); })
      .catch(() => { if (!cancelled) setTools([]); });
    return () => { cancelled = true; };
  }, [open, tools, user]);

  // Callie articles and people, from the server, a moment after typing stops. An older answer never replaces a newer question.
  useEffect(() => {
    if (!open || !typed) {
      sequence.current += 1;
      setRemote(NONE);
      setSearching(false);
      return undefined;
    }
    const mine = sequence.current + 1;
    sequence.current = mine;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await api.get('/api/search', { params: { q: query.trim() } });
        if (sequence.current !== mine) return;
        setRemote({ callie: (res.data && res.data.callie) || [], people: (res.data && res.data.people) || [] });
        setFailed(false);
      } catch (err) {
        if (sequence.current !== mine) return;
        setRemote(NONE);
        setFailed(true);
      } finally {
        if (sequence.current === mine) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, typed, query]);

  const staticEntries = useMemo(() => [
    ...pageEntries(user),
    ...adminEntries(user),
    ...linkEntries(tools)
  ], [user, tools]);

  // Every group with everything it has (the kinds in order; or, with nothing typed, the recent things and the modules).
  const { groups, corrected } = useMemo(() => {
    if (tokens.length) {
      const local = searchLocal(staticEntries, query, { limit: PER_KIND });
      const found = [...local.results, ...callieEntries(remote.callie).slice(0, PER_KIND), ...personEntries(remote.people).slice(0, PER_KIND)];
      const list = KINDS.map((k) => ({ header: k.label, entries: found.filter((e) => e.kind === k.id) })).filter((g) => g.entries.length);
      return { groups: list, corrected: local.corrected && !remote.callie.length && !remote.people.length };
    }
    // Nothing typed: what was opened from here last (those still allowed), then the modules.
    const known = new Set(staticEntries.map((e) => e.id));
    const back = recent.filter((e) => (e.kind === 'callie' || e.kind === 'person' ? true : known.has(e.id)));
    const list = [];
    if (back.length) list.push({ header: 'Recent', entries: back.map((entry) => ({ ...entry, id: entry.id, recent: true })) });
    list.push({ header: 'Go to', entries: staticEntries.filter((e) => e.id.startsWith('module:')) });
    return { groups: list, corrected: false };
  }, [tokens, query, staticEntries, remote, recent]);

  // The panel never scrolls. It starts with every row there is (a few of each kind) and the layout effect below takes one away at a time until what is on show
  // fits, so only as many go as must. The rows that stay are shared round-robin between the kinds (one each, then a second each ...), so no kind is emptied while
  // another keeps five. A new answer (Callie and People arrive a moment after the pages) starts again from every row, so the room is always used.
  const total = groups.reduce((sum, g) => sum + g.entries.length, 0);
  const fitKey = `${typed ? query.trim() : ''}|${groups.map((g) => g.entries.length).join(',')}`;
  const budget = fit.key === fitKey ? fit.n : total;
  // While the server's answer (Callie, People) is on its way, its place is kept with loading rows, so the pages found at once do not take room the answer then
  // takes back: the list holds still as the answer arrives.
  const ghosts = typed && searching ? KINDS.filter((k) => (k.id === 'callie' || k.id === 'person') && !groups.some((g) => g.header === k.label)).map((k) => k.label) : [];
  const rows = useMemo(() => {
    const take = groups.map(() => 0);
    let left = budget;
    for (let round = 0; left > 0 && round < PER_KIND; round += 1) {
      for (let i = 0; i < groups.length && left > 0; i += 1) {
        if (groups[i].entries.length > round) { take[i] += 1; left -= 1; }
      }
    }
    const out = [];
    const ordered = ghosts.length
      ? KINDS.map((k) => groups.find((g) => g.header === k.label) || (ghosts.includes(k.label) ? { header: k.label, entries: [], ghost: true } : null)).filter(Boolean)
      : groups;
    ordered.forEach((g) => {
      if (g.ghost) {
        out.push({ header: g.header });
        out.push({ ghost: `${g.header}-1` }, { ghost: `${g.header}-2` });
        return;
      }
      const taken = take[groups.indexOf(g)];
      if (!taken) return;
      out.push({ header: g.header });
      g.entries.slice(0, taken).forEach((entry) => out.push({ entry }));
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `ghosts` is made from `groups` and `searching`, which are listed
  }, [groups, budget, typed, searching]);
  const results = useMemo(() => rows.filter((row) => row.entry).map((row) => row.entry), [rows]);
  const hidden = Math.max(0, total - results.length);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every render on purpose: it only ever takes one row away, so it stops once everything fits
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!open || !el || budget <= 1 || el.scrollHeight <= el.clientHeight + 1) return;
    setFit({ key: fitKey, n: budget - 1 });
  });

  useEffect(() => { setActive(0); moved.current = false; }, [query]);

  const choose = (entry) => {
    if (!entry) return;
    writeRecent(user, entry);
    setOpen(false);
    if (entry.href) window.open(entry.href, '_blank', 'noopener,noreferrer');
    else if (entry.to) navigate(entry.to);
  };

  // The full results page: everything found, in its own page (the box keeps only a few of each kind).
  const showAll = () => {
    if (!typed) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moved.current = true;
      if (results.length) setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moved.current = true;
      if (results.length) setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Typed, and the highlight not moved by hand: the full page. Otherwise (arrow keys used, or nothing typed yet) the highlighted row opens.
      if (typed && !moved.current) showAll();
      else choose(results[active]);
    } else if (e.key === 'Tab') {
      e.preventDefault(); // the box is the one stop, so Tab cannot send focus to the page behind it
    }
  };

  let optionIndex = -1;
  const listId = 'global-search-list';
  const nothing = typed && !searching && results.length === 0;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`suite-search-btn${compact ? ' suite-search-btn--compact' : ''}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label="Search"
        title="Search (Ctrl K)"
      >
        <AdminIcon name="search" size={16} />
        {!compact && <span className="suite-search-btn__label">Search</span>}
        {!compact && <kbd className="suite-search-btn__kbd">Ctrl K</kbd>}
      </button>
      {pop.mounted && typeof document !== 'undefined' && createPortal(
        <div
          className={`gs ui-pop-fade${pop.closing ? ' is-closing' : ''}`}
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div
            className={`gs__panel ui-pop${anchor ? ' gs__panel--anchored' : ''}${pop.closing ? ' is-closing' : ''}`}
            style={anchor ? { top: anchor.top, right: anchor.right, maxHeight: `min(36rem, calc(100vh - ${anchor.top}px - 1rem))` } : undefined}
            role="dialog"
            aria-modal="true"
            aria-label="Search"
          >
            <div className="gs__field">
              <AdminIcon name="search" size={18} />
              <input
                ref={inputRef}
                type="text"
                className="gs__input"
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-activedescendant={results[active] ? `gs-opt-${active}` : undefined}
                aria-label="Search pages, articles, people and settings"
                placeholder="Search pages, articles, people and settings"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                spellCheck="false"
                maxLength={80}
              />
              {searching ? <span className="gs__spinner" role="status" aria-label="Searching" /> : <kbd className="gs__kbd">Esc</kbd>}
            </div>
            {corrected && <div className="gs__note" role="status">No exact match for “{query.trim()}”. Showing the closest.</div>}
            {failed && <div className="gs__note gs__note--warn" role="status">Articles and people could not be searched just now. Pages and settings are still listed.</div>}
            <div className="gs__list" id={listId} role="listbox" ref={listRef}>
              {rows.map((row, i) => {
                if (row.header !== undefined) return <div key={`h-${i}`} className="gs__group" role="presentation">{row.header}</div>;
                if (row.ghost) return <div key={row.ghost} className="gs__ghost" aria-hidden="true"><SkeletonBlock height="1.2rem" /></div>;
                optionIndex += 1;
                const index = optionIndex;
                const { entry } = row;
                const isActive = index === active;
                return (
                  <button
                    key={`${entry.id}${entry.recent ? ':recent' : ''}`}
                    id={`gs-opt-${index}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-active={isActive ? 'true' : 'false'}
                    tabIndex={-1}
                    className={`gs__row${isActive ? ' is-active' : ''}`}
                    onMouseMove={() => { if (!isActive) setActive(index); }}
                    onClick={() => choose(entry)}
                  >
                    <RowIcon entry={entry} />
                    <span className="gs__text">
                      <span className="gs__title">
                        <Highlight text={entry.title} tokens={tokens} />
                        {entry.archived ? <em className="gs__tag">Archived</em> : null}
                      </span>
                      {entry.description ? <span className="gs__desc"><Highlight text={entry.description} tokens={tokens} /></span> : null}
                    </span>
                    {entry.subtitle ? <span className="gs__path">{entry.subtitle}</span> : null}
                    {entry.href ? <span className="gs__out" aria-hidden="true">↗</span> : null}
                  </button>
                );
              })}
              {nothing && (
                <div className="gs__empty">Nothing matches “{query.trim()}” among the pages, articles and people you can open.</div>
              )}
              {tokens.length > 0 && !typed && (
                <div className="gs__empty">Keep typing: at least {MIN_QUERY} letters.</div>
              )}
            </div>
            <div className="gs__foot">
              {typed ? (
                <>
                  <button type="button" className="btn btn-primary btn-sm" onClick={showAll}>See all results</button>
                  <span className="gs__foot-hint">{hidden > 0 ? `${hidden} more there. ` : ''}Or press <kbd>Enter</kbd></span>
                </>
              ) : (
                <span className="gs__foot-hint"><kbd>↑</kbd><kbd>↓</kbd> to move <kbd>Enter</kbd> to open</span>
              )}
              <span className="gs__foot-hint gs__foot-end"><kbd>Esc</kbd> to close</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
